"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";

const RE_WHITESPACE = /\s/g;

/*
 * "Letters" and "Digits" by Unicode property, not by `a-zA-Z` and `0-9`.
 *
 * The two stat cards read 0 for any text with no ASCII in it: a reader pasting Russian,
 * Arabic, Hindi, Korean, Chinese or Japanese saw a "Letters" card sitting at zero next
 * to a "Characters" card counting hundreds. Accented Latin under-counted instead of
 * zeroing, which reads as working: "Grüße aus Köln" reported 9 letters of 12,
 * "İstanbul'da güzel şeyler" 18 of 21, and "Đây là tiêu đề" 5 of 11.
 *
 * `\p{L}` alone, WITHOUT `\p{M}` — the opposite of the word-boundary classes elsewhere
 * in this fleet, and deliberately so. The question here is "how many letters", and a
 * combining mark is not one: `"café"` in NFD is five code points and four letters, and a
 * Devanagari matra is a vowel sign attached to a letter rather than a letter of its own.
 * The marks are still counted by the Characters cards, which is where they belong.
 *
 * `\p{Nd}` rather than `\p{N}`, so the Digits card counts the Arabic-Indic and
 * Devanagari digits a reader actually types (٢٠٢٤, २०२४) and not `½` or `Ⅶ`.
 */
const RE_LETTERS_ONLY = /\p{L}/gu;
const RE_DIGITS_ONLY = /\p{Nd}/gu;

interface CharStats {
  total: number;
  noSpaces: number;
  unique: number;
  letters: number;
  digits: number;
  spaces: number;
}

function computeCharStats(text: string): CharStats {
  const total = text.length;
  const noSpaces = text.replace(RE_WHITESPACE, "").length;
  const unique = new Set(text).size;
  const letters = (text.match(RE_LETTERS_ONLY) ?? []).length;
  const digits = (text.match(RE_DIGITS_ONLY) ?? []).length;
  const spaces = total - noSpaces;
  return { total, noSpaces, unique, letters, digits, spaces };
}

function getCharFrequency(text: string): { char: string; count: number }[] {
  if (text.length === 0) {
    return [];
  }
  const freq: Record<string, number> = {};
  for (const ch of text) {
    if (ch === " " || ch === "\n" || ch === "\t") {
      continue;
    }
    freq[ch] = (freq[ch] ?? 0) + 1;
  }
  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 20)
    .map(([char, count]) => ({ char, count }));
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const CHARACTER_COUNTER_STRINGS = {
  textToCountCharactersIn: "Text to count characters in",
  pasteOrTypeYourText: "Paste or type your text here...",
  charactersWithSpaces: "Characters (with spaces)",
  charactersNoSpaces: "Characters (no spaces)",
  uniqueCharacters: "Unique Characters",
  letters: "Letters",
  digits: "Digits",
  spaces: "Spaces",
  characterFrequencyTop20: "Character Frequency (top 20)",
  commonCharacterLimits: "Common Character Limits",
  limitTwitter: "Twitter / X",
  limitInstagram: "Instagram caption",
  limitMetaDescription: "Meta description",
  limitCommonApp: "Common App essay",
  limitLinkedIn: "LinkedIn headline",
  limitSms: "SMS message",
  overLimit: "{count} over limit",
  remaining: "{count} remaining",
} as const;

type Strings = { [K in keyof typeof CHARACTER_COUNTER_STRINGS]: string };

const LIMITS: { label: keyof Strings; limit: number }[] = [
  { label: "limitTwitter", limit: 280 },
  { label: "limitInstagram", limit: 2200 },
  { label: "limitMetaDescription", limit: 160 },
  { label: "limitCommonApp", limit: 3250 },
  { label: "limitLinkedIn", limit: 220 },
  { label: "limitSms", limit: 160 },
];

export default function CharacterCounterWidget() {
  const [text, setText] = useState("");

  const stats = computeCharStats(text);
  const freq = getCharFrequency(text);

  const strings = useToolUi(CHARACTER_COUNTER_STRINGS);

  const fmt = useToolNumber();

  return (
    <>
      <textarea
        aria-label={strings.textToCountCharactersIn}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.pasteOrTypeYourText}
        value={text}
      />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard
          label={strings.charactersWithSpaces}
          value={fmt(stats.total)}
        />
        <StatCard
          label={strings.charactersNoSpaces}
          value={fmt(stats.noSpaces)}
        />
        <StatCard label={strings.uniqueCharacters} value={fmt(stats.unique)} />
        <StatCard label={strings.letters} value={fmt(stats.letters)} />
        <StatCard label={strings.digits} value={fmt(stats.digits)} />
        <StatCard label={strings.spaces} value={fmt(stats.spaces)} />
      </div>
      {text.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.characterFrequencyTop20}
          </h2>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-10">
            {freq.map((item) => (
              <div
                className="rounded-lg border border-border bg-card px-2 py-2 text-center"
                key={item.char}
              >
                <p className="font-medium text-foreground text-sm">
                  {item.char === " " ? "␣" : item.char}
                </p>
                <p className="text-muted-foreground text-xs">
                  {fmt(item.count)}×
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.commonCharacterLimits}
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {LIMITS.map(({ label, limit }) => {
            const used = stats.total;
            const remaining = limit - used;
            const over = remaining < 0;
            return (
              <div
                className="rounded-lg border border-border bg-card p-3"
                key={label}
              >
                <p className="font-medium text-foreground text-sm">
                  {strings[label]}
                </p>
                <p
                  className={`mt-1 text-xs ${over ? "text-destructive" : "text-muted-foreground"}`}
                >
                  {over
                    ? t(strings.overLimit, { count: fmt(Math.abs(remaining)) })
                    : t(strings.remaining, { count: fmt(remaining) })}{" "}
                  <span className="text-muted-foreground">/ {fmt(limit)}</span>
                </p>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
